import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Avatar } from '@/components/brand/Brand';
import { Button } from '@/components/ui/Button';
import type { GroupParticipant, GroupRound, TeamId } from '@/lib/api/types';
import { cx } from '@/lib/cx';
import { EM_DASH } from '@/lib/format/number';
import { t } from '@/lib/i18n';
import { holeOf, scorableParticipants, strokesReceivedOnHole } from './groupMath';
import styles from './GroupScoreGrid.module.css';
import { CheckIcon, CloseIcon } from '@/components/icons';

/**
 * O scorecard em grupo como grade de desktop (canvas 1y, estendido pelo 2c).
 *
 * A grade **inverte a hierarquia do celular**. Lá o cartão mostra um buraco e quatro jogadores;
 * aqui mostra quatro jogadores e dezoito buracos, que é o que o desktop existe para fazer. As
 * linhas de dupla ficam acima dos membros e carregam o melhor gross de cada buraco em negrito,
 * então dá para ler a partida na horizontal sem somar nada.
 *
 * ## O que o 2c acrescentou
 *
 * **Os detalhes deixaram de ser só seus.** Havia uma linha de putts, e ela era do dono — os
 * outros três nunca tiveram esse dado. Agora putts, fairway, penalidade e bunker são duas linhas
 * abaixo da grade, **do buraco em foco**, com uma célula por jogador. Espalhá-los pelos dezoito
 * buracos daria setenta e duas células por jogador para um dado que se preenche um buraco por vez;
 * no celular isso é acordeão porque não cabe, e aqui cabe tudo à vista de uma vez só.
 *
 * **O buraco corrente é largo, e o futuro é tracejado.** Dezoito colunas idênticas não dizem onde
 * o flight está; a largura diz. O tracejado marca o que ainda não aconteceu sem escurecer nada,
 * porque essas células continuam clicáveis — marcar adiantado é permitido, só não é o padrão.
 *
 * **Setas andam entre buracos.** Tab já andava entre jogadores, porque as células de um buraco são
 * vizinhas no DOM; faltava o outro eixo. `←`/`→` movem a mesma linha para o buraco anterior e o
 * seguinte, e `Enter` continua gravando e descendo o cartão.
 */

type DetailField = 'putts' | 'fairwayHit' | 'penaltyStrokes' | 'bunkerShots';

type Row =
  | { kind: 'par' }
  | { kind: 'si' }
  | { kind: 'team'; team: TeamId; label: string; editable: boolean }
  | { kind: 'player'; participant: GroupParticipant; you: boolean; editable: boolean }
  | { kind: 'teamPutts'; label: string };

/** De onde vem o número daquela linha — o que as etiquetas do 2c dizem em uma palavra. */
type PlayerTag = 'marker' | 'app' | 'noAccount' | null;

/** Uma linha de jogador pronta para o `PATCH` — a mesma forma que o cartão do celular manda. */
export interface GridHoleEntry {
  participantId: string;
  strokes: number;
  putts?: number;
  fairwayHit?: boolean;
  penaltyStrokes?: number;
  bunkerShots?: number;
}

export interface GridTeamEntry {
  team: TeamId;
  strokes: number;
  putts?: number | null;
}

export interface GroupGridProps {
  group: GroupRound;
  /** Grava um buraco inteiro. Listas vazias significam "nada preenchido aqui ainda". */
  onCommit: (holeNumber: number, entries: GridHoleEntry[], teamEntries: GridTeamEntry[]) => void;
  onExit: () => void;
  onReview: () => void;
  saving: boolean;
}

export function GroupScoreGrid({ group, onCommit, onExit, onReview, saving }: GroupGridProps) {
  const [draft, setDraft] = useState<Record<string, number>>({});
  /** Fairway é tri-estado, então não cabe no mesmo mapa dos números. */
  const [fwDraft, setFwDraft] = useState<Record<string, boolean | null>>({});
  const inputs = useRef(new Map<string, HTMLInputElement>());

  const scramble = group.format.contest === 'teams' && group.format.teamFormat === 'scramble';
  const teams = teamsOf(group);
  const holes = group.holes;

  const scorable = useMemo(() => scorableParticipants(group), [group]);
  const scorableIds = useMemo(() => new Set(scorable.map((p) => p.id)), [scorable]);

  const rows = buildRows(group, { scramble, teams, scorableIds });

  const currentHole = firstUnscoredHole(group, scorable);
  /*
   * O buraco a que as linhas de detalhe se referem. Começa no corrente e segue a célula em foco:
   * quem clica no 7 para corrigir uma tacada quer os putts do 7 logo abaixo, não os do 12.
   */
  const [focusedHole, setFocusedHole] = useState<number | null>(null);
  const detailHole = focusedHole ?? currentHole ?? holes[holes.length - 1]?.holeNumber ?? 1;
  const detailPar = holes.find((h) => h.holeNumber === detailHole)?.par ?? 4;

  const numberAt = (holeNumber: number, key: string): number | null => {
    const drafted = draft[`${holeNumber}:${key}`];
    if (drafted !== undefined) return drafted;
    return storedValue(group, key, holeNumber);
  };

  const fwAt = (holeNumber: number, participantId: string): boolean | null => {
    const drafted = fwDraft[`${holeNumber}:${participantId}`];
    if (drafted !== undefined) return drafted;
    return holeOf(group, { id: participantId }, holeNumber)?.fairwayHit ?? null;
  };

  const commitHole = (holeNumber: number) => {
    const par = holes.find((h) => h.holeNumber === holeNumber)?.par ?? 4;

    const entries: GridHoleEntry[] = [];
    if (!scramble) {
      for (const p of scorable) {
        const strokes = numberAt(holeNumber, p.id);
        if (strokes === null) continue;

        const entry: GridHoleEntry = { participantId: p.id, strokes };
        const putts = numberAt(holeNumber, detailKey(p.id, 'putts'));
        const penalty = numberAt(holeNumber, detailKey(p.id, 'penaltyStrokes'));
        const bunker = numberAt(holeNumber, detailKey(p.id, 'bunkerShots'));
        if (putts !== null) entry.putts = putts;
        if (penalty !== null) entry.penaltyStrokes = penalty;
        if (bunker !== null) entry.bunkerShots = bunker;

        /*
         * Par 3 não tem fairway a acertar, e o backend força nulo ali de qualquer jeito. Mandar
         * `false` seria registrar uma tacada perdida que a regra nem contabiliza.
         */
        const fw = fwAt(holeNumber, p.id);
        if (par > 3 && fw !== null) entry.fairwayHit = fw;

        entries.push(entry);
      }
    }

    const teamEntries: GridTeamEntry[] = scramble
      ? teams
          .map((team) => ({ team, strokes: numberAt(holeNumber, `team:${team}`) }))
          .filter((e): e is { team: TeamId; strokes: number } => e.strokes !== null)
          .map((e) => ({ ...e, putts: numberAt(holeNumber, TEAM_PUTTS_KEY) }))
      : [];

    onCommit(holeNumber, entries, teamEntries);
  };

  const focusCell = (holeNumber: number, key: string) => {
    const next = inputs.current.get(`${holeNumber}:${key}`);
    next?.focus();
    next?.select();
  };

  const onCellKey = (e: KeyboardEvent<HTMLInputElement>, holeNumber: number, key: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitHole(holeNumber);
      focusCell(holeNumber + 1, key);
      return;
    }

    /*
     * O eixo horizontal do 2c. Sobrescrever as setas num `<input type="number">` é seguro aqui
     * porque a célula seleciona o conteúdo inteiro ao ganhar foco — não há cursor para mover
     * dentro dela — e o que o teclado precisa oferecer numa grade é o buraco vizinho.
     */
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      const step = e.key === 'ArrowLeft' ? -1 : 1;
      const target = inputs.current.get(`${holeNumber + step}:${key}`);
      if (!target) return;
      e.preventDefault();
      commitHole(holeNumber);
      target.focus();
      target.select();
    }
  };

  const segments = segmentsFor(holes.length);

  return (
    <div className={styles.wrap}>
      <div className={styles.scroller}>
        <div className={styles.grid}>
          <div className={styles.labels}>
            <div className={styles.headLabel}>{t('scorecard.hole')}</div>
            {rows.map((row) => (
              <div key={rowKey(row)} className={cx(styles.cell, styles.labelCell, rowClass(row))}>
                <RowLabel row={row} group={group} />
              </div>
            ))}
          </div>

          {holes.map((h) => (
            <div
              key={h.holeNumber}
              className={cx(
                styles.col,
                h.holeNumber === currentHole && styles.colNow,
                currentHole !== null && h.holeNumber > currentHole && styles.colFuture,
              )}
            >
              <div className={cx(styles.cell, styles.head)}>{h.holeNumber}</div>
              {rows.map((row) => {
                const key = rowKey(row);
                const editable = isEditable(row);

                return (
                  <div key={key} className={cx(styles.cell, rowClass(row))}>
                    {row.kind === 'par' ? (
                      h.par
                    ) : row.kind === 'si' ? (
                      h.strokeIndex
                    ) : row.kind === 'team' && !row.editable ? (
                      <span className={styles.teamBest}>
                        {bestGrossOf(group, row.team, h.holeNumber) ?? EM_DASH}
                      </span>
                    ) : editable ? (
                      <input
                        ref={(el) => {
                          const id = `${h.holeNumber}:${key}`;
                          if (el) inputs.current.set(id, el);
                          else inputs.current.delete(id);
                        }}
                        className={cx(
                          styles.input,
                          isStrokeRow(row) &&
                            scoreClass(numberAt(h.holeNumber, key), h.par, styles),
                        )}
                        type="number"
                        inputMode="numeric"
                        min={isStrokeRow(row) ? 1 : 0}
                        max={20}
                        value={numberAt(h.holeNumber, key) ?? ''}
                        placeholder={isStrokeRow(row) ? String(h.par) : ''}
                        aria-label={cellLabel(row, h.holeNumber)}
                        onChange={(e) => {
                          if (e.target.value === '') return;
                          const v = Number(e.target.value);
                          if (!Number.isFinite(v) || v < 0 || v > 20) return;
                          setDraft((prev) => ({ ...prev, [`${h.holeNumber}:${key}`]: v }));
                        }}
                        onKeyDown={(e) => onCellKey(e, h.holeNumber, key)}
                        onBlur={() => commitHole(h.holeNumber)}
                        onFocus={(e) => {
                          e.target.select();
                          setFocusedHole(h.holeNumber);
                        }}
                      />
                    ) : (
                      (numberAt(h.holeNumber, key) ?? EM_DASH)
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          {segments.map((seg) => (
            <div key={seg.key} className={cx(styles.col, styles.colTotal)}>
              <div className={cx(styles.cell, styles.head)}>{seg.key}</div>
              {rows.map((row) => {
                const key = rowKey(row);
                const sum = totalFor(holes, seg, (h) =>
                  row.kind === 'par'
                    ? h.par
                    : row.kind === 'si'
                      ? null
                      : row.kind === 'team' && !row.editable
                        ? bestGrossOf(group, row.team, h.holeNumber)
                        : numberAt(h.holeNumber, key),
                );
                return (
                  <div key={key} className={cx(styles.cell, rowClass(row), styles.totalValue)}>
                    {sum ?? EM_DASH}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/*
        As duas linhas do 2c: putts numa, FW · pen · bunker na outra, uma célula por jogador e
        tudo referente ao buraco em foco. Ficam fora do rolador porque são do buraco, não da
        volta — rolar dezoito colunas para achá-las seria procurar o que já está na frente.
      */}
      {!scramble && scorable.length > 0 && (
        <div className={styles.details}>
          <div className={styles.detailRow}>
            <div className={styles.detailRowLabel}>
              {t('record.grid.puttsOnHole', { number: detailHole })}
            </div>
            {scorable.map((p) => (
              <div key={p.id} className={styles.detailCell}>
                <span className={styles.detailWho}>{shortNameOf(group, p)}</span>
                <DetailInput
                  value={numberAt(detailHole, detailKey(p.id, 'putts'))}
                  label={t('record.group.detailCellAria', {
                    field: t('round.putts'),
                    name: p.displayName,
                    number: detailHole,
                  })}
                  onChange={(v) =>
                    setDraft((prev) => ({
                      ...prev,
                      [`${detailHole}:${detailKey(p.id, 'putts')}`]: v,
                    }))
                  }
                  onCommit={() => commitHole(detailHole)}
                />
              </div>
            ))}
          </div>

          <div className={styles.detailRow}>
            <div className={styles.detailRowLabel}>
              {t('record.grid.fwPenBunker')}
              {/* Par 3 não tem fairway a acertar, e o servidor guarda `null` — nunca "errou". */}
              {detailPar <= 3 && (
                <span className={styles.detailRowNote}>{t('record.grid.fwHiddenPar3')}</span>
              )}
            </div>
            {scorable.map((p) => (
              <div key={p.id} className={styles.detailCell}>
                <span className={styles.detailWho}>{shortNameOf(group, p)}</span>
                <FairwayCell
                  value={fwAt(detailHole, p.id)}
                  par={detailPar}
                  editable
                  label={t('record.group.detailCellAria', {
                    field: t('round.fairway'),
                    name: p.displayName,
                    number: detailHole,
                  })}
                  onCycle={(next) =>
                    setFwDraft((prev) => ({ ...prev, [`${detailHole}:${p.id}`]: next }))
                  }
                  onCommit={() => commitHole(detailHole)}
                />
                <DetailInput
                  value={numberAt(detailHole, detailKey(p.id, 'penaltyStrokes'))}
                  label={t('record.group.detailCellAria', {
                    field: t('round.penalty'),
                    name: p.displayName,
                    number: detailHole,
                  })}
                  onChange={(v) =>
                    setDraft((prev) => ({
                      ...prev,
                      [`${detailHole}:${detailKey(p.id, 'penaltyStrokes')}`]: v,
                    }))
                  }
                  onCommit={() => commitHole(detailHole)}
                />
                <DetailInput
                  value={numberAt(detailHole, detailKey(p.id, 'bunkerShots'))}
                  label={t('record.group.detailCellAria', {
                    field: t('round.bunker'),
                    name: p.displayName,
                    number: detailHole,
                  })}
                  onChange={(v) =>
                    setDraft((prev) => ({
                      ...prev,
                      [`${detailHole}:${detailKey(p.id, 'bunkerShots')}`]: v,
                    }))
                  }
                  onCommit={() => commitHole(detailHole)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.footer}>
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={cx(styles.legendChip, styles.birdie)} aria-hidden="true" />
            {t('scorecard.legend.birdie')}
          </span>
          <span className={styles.legendItem}>
            <span className={cx(styles.legendChip, styles.bogey)} aria-hidden="true" />
            {t('scorecard.legend.bogey')}
          </span>
          <span className={styles.legendItem}>
            <span className={cx(styles.legendChip, styles.double)} aria-hidden="true" />
            {t('scorecard.legend.double')}
          </span>
          <span className={styles.legendItem}>
            <span className={cx(styles.legendChip, styles.legendFuture)} aria-hidden="true" />
            {t('record.grid.notPlayed')}
          </span>
          <span>{t('record.grid.legendKeys')}</span>
          {group.format.handicapMode === 'net' && <span>{t('record.group.legendStrokes')}</span>}
          {teams.length > 0 && !scramble && <span>{t('record.group.legendBestBall')}</span>}
        </div>
        <div className={styles.actions}>
          <Button variant="secondary" onClick={onExit}>
            {t('record.grid.oneAtATime')}
          </Button>
          <Button busy={saving} onClick={onReview}>
            {t('record.group.saveAndExit')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Fairway numa célula de 40 px: um botão que cicla `— → acertou → errou → —`.
 *
 * Ícone e não caractere, pela mesma razão de todo o resto do app: `✓` depende de a fonte do
 * aparelho ter o glifo, e onde não tem o botão vira uma caixinha. O `aria-label` carrega o
 * significado, então o leitor de tela não depende do desenho.
 */
function FairwayCell({
  value,
  par,
  editable,
  label,
  onCycle,
  onCommit,
}: {
  value: boolean | null;
  par: number;
  editable: boolean;
  label: string;
  onCycle: (next: boolean | null) => void;
  onCommit: () => void;
}) {
  // Par 3 não tem fairway a acertar — a célula fica vazia, não "errou".
  if (par <= 3) return <span className={styles.fwNone}>{EM_DASH}</span>;

  const mark =
    value === true ? (
      <CheckIcon size={13} />
    ) : value === false ? (
      <CloseIcon size={13} />
    ) : (
      <span className={styles.fwNone}>{EM_DASH}</span>
    );

  if (!editable) return <span className={styles.fwStatic}>{mark}</span>;

  return (
    <button
      type="button"
      className={cx(styles.fwButton, value === true && styles.fwHit, value === false && styles.fwMiss)}
      aria-label={label}
      onClick={() => {
        onCycle(value === null ? true : value === true ? false : null);
        onCommit();
      }}
    >
      {mark}
    </button>
  );
}

/** Um número de detalhe (putts, penalidade, bunker) numa célula estreita. */
function DetailInput({
  value,
  label,
  onChange,
  onCommit,
}: {
  value: number | null;
  label: string;
  onChange: (value: number) => void;
  onCommit: () => void;
}) {
  return (
    <input
      className={styles.detailInput}
      type="number"
      inputMode="numeric"
      min={0}
      max={20}
      value={value ?? ''}
      aria-label={label}
      onChange={(e) => {
        if (e.target.value === '') return;
        const v = Number(e.target.value);
        if (!Number.isFinite(v) || v < 0 || v > 20) return;
        onChange(v);
      }}
      onBlur={onCommit}
      onFocus={(e) => e.target.select()}
    />
  );
}

const TEAM_PUTTS_KEY = 'teamPutts';

function detailKey(participantId: string, field: DetailField): string {
  return `${participantId}:${field}`;
}

/**
 * De onde vem o número de cada linha, dito em uma etiqueta.
 *
 * O marcador precisa saber quando uma linha **saiu da responsabilidade dele** — o jogador entrou
 * e marca sozinho — senão continua anotando por cima. É o mesmo aviso que o 2b dá em texto.
 */
function tagOf(group: GroupRound, p: GroupParticipant): PlayerTag {
  if (p.id === group.viewer.participantId) return group.viewer.canScoreAll ? 'marker' : null;
  if (p.kind === 'guest' || p.roundId === null) return 'noAccount';
  return 'app';
}

function shortNameOf(group: GroupRound, p: GroupParticipant): string {
  return p.id === group.viewer.participantId ? t('record.you') : firstNameOf(p.displayName);
}

function buildRows(
  group: GroupRound,
  ctx: {
    scramble: boolean;
    teams: TeamId[];
    scorableIds: Set<string>;
  },
): Row[] {
  const rows: Row[] = [{ kind: 'par' }, { kind: 'si' }];
  const you = group.viewer.participantId;

  if (ctx.scramble) {
    for (const team of ctx.teams) {
      rows.push({ kind: 'team', team, label: namesOf(group, team), editable: true });
    }
    rows.push({ kind: 'teamPutts', label: t('record.group.teamPutts') });
    return rows;
  }

  const pushPlayer = (p: GroupParticipant) => {
    rows.push({
      kind: 'player',
      participant: p,
      you: p.id === you,
      editable: ctx.scorableIds.has(p.id),
    });
  };

  if (ctx.teams.length > 0) {
    for (const team of ctx.teams) {
      rows.push({ kind: 'team', team, label: namesOf(group, team), editable: false });
      for (const p of group.participants.filter((x) => x.team === team)) pushPlayer(p);
    }
  } else {
    for (const p of group.participants) pushPlayer(p);
  }

  return rows;
}

function rowKey(row: Row): string {
  switch (row.kind) {
    case 'par':
      return 'par';
    case 'si':
      return 'si';
    case 'team':
      return `team:${row.team}`;
    case 'player':
      return row.participant.id;
    case 'teamPutts':
      return TEAM_PUTTS_KEY;
  }
}

/** Linhas cujo número é uma tacada — as únicas que ganham a cor de birdie/bogey. */
function isStrokeRow(row: Row): boolean {
  return row.kind === 'player' || row.kind === 'team';
}

function isEditable(row: Row): boolean {
  switch (row.kind) {
    case 'player':
      return row.editable;
    case 'team':
      return row.editable;
    case 'teamPutts':
      return true;
    default:
      return false;
  }
}

function rowClass(row: Row): string | undefined {
  switch (row.kind) {
    case 'par':
      return styles.rowPar;
    case 'si':
      return styles.rowSi;
    case 'team':
      return styles.rowTeam;
    case 'player':
      return row.you ? cx(styles.rowPlayer, styles.rowYou) : styles.rowPlayer;
    case 'teamPutts':
      return styles.rowPutts;
  }
}

function RowLabel({ row, group }: { row: Row; group: GroupRound }) {
  if (row.kind === 'par') return <>{t('scorecard.par')}</>;
  if (row.kind === 'si') return <>{t('record.group.strokeIndexRow')}</>;
  if (row.kind === 'teamPutts') return <>{row.label}</>;

  if (row.kind === 'team') {
    return (
      <>
        <span className={styles.teamBadge}>{row.team}</span>
        <span className={styles.labelText}>{row.label}</span>
      </>
    );
  }

  const p = row.participant;
  const received =
    group.format.handicapMode === 'net' && p.courseHandicap !== null
      ? strokesReceivedOnHole(p.courseHandicap, 1, group.holesPlayed)
      : 0;

  const tag = tagOf(group, p);

  return (
    <>
      <Avatar name={p.displayName} size={22} variant={row.you ? 'gold' : 'green'} />
      <span className={styles.labelText}>{row.you ? t('record.you') : p.displayName}</span>
      {tag && <span className={styles.labelTag}>{t(TAG_LABEL[tag])}</span>}
      {received > 0 && (
        <span className={styles.labelStrokes}>
          {received} {t('record.group.strokesShort')}
        </span>
      )}
    </>
  );
}

const TAG_LABEL = {
  marker: 'record.grid.tagMarker',
  app: 'record.grid.tagApp',
  noAccount: 'record.grid.tagNoAccount',
} as const;

function cellLabel(row: Row, holeNumber: number): string {
  if (row.kind === 'teamPutts') return t('record.group.puttsHoleAria', { number: holeNumber });
  if (row.kind === 'team') {
    return t('record.group.teamStrokesAria', { team: row.team, number: holeNumber });
  }
  if (row.kind === 'player') {
    const who = row.you ? t('record.youLower') : row.participant.displayName;
    return t('record.group.playerStrokesAria', { name: who, number: holeNumber });
  }
  return t('hole.number', { number: holeNumber });
}

function storedValue(group: GroupRound, key: string, holeNumber: number): number | null {
  if (key === TEAM_PUTTS_KEY) {
    const team = group.participants.find((p) => p.kind === 'owner')?.team ?? null;
    if (!team) return null;
    return group.teamScores[team].find((s) => s.holeNumber === holeNumber)?.putts ?? null;
  }

  if (key.startsWith('team:')) {
    const team = key.slice(5) as TeamId;
    return group.teamScores[team].find((s) => s.holeNumber === holeNumber)?.strokes ?? null;
  }

  const [participantId, field] = key.split(':');
  const saved = holeOf(group, { id: participantId as string }, holeNumber);
  if (!saved) return null;
  if (!field) return saved.strokes;
  if (field === 'putts') return saved.putts ?? null;
  if (field === 'penaltyStrokes') return saved.penaltyStrokes ?? null;
  if (field === 'bunkerShots') return saved.bunkerShots ?? null;
  return null;
}

/**
 * Em que buraco o flight está — a coluna larga.
 *
 * É o primeiro buraco sem tacada de quem está marcando: é onde a próxima entrada vai. Quando
 * todos estão preenchidos não há corrente, e a grade volta a ter dezoito colunas iguais, que é
 * o certo para um cartão pronto.
 */
function firstUnscoredHole(group: GroupRound, scorable: GroupParticipant[]): number | null {
  const reference = scorable[0] ?? group.participants.find((p) => p.kind === 'owner');
  if (!reference) return null;
  for (const h of group.holes) {
    if (!holeOf(group, reference, h.holeNumber)) return h.holeNumber;
  }
  return null;
}

/** O melhor gross da dupla no buraco — o número em negrito da linha de dupla. */
function bestGrossOf(group: GroupRound, team: TeamId, holeNumber: number): number | null {
  const values = group.participants
    .filter((p) => p.team === team)
    .map((p) => storedValue(group, p.id, holeNumber))
    .filter((v): v is number => v !== null);
  return values.length === 0 ? null : Math.min(...values);
}

function namesOf(group: GroupRound, team: TeamId): string {
  return group.participants
    .filter((p) => p.team === team)
    .map((p) => (p.id === group.viewer.participantId ? t('record.you') : firstNameOf(p.displayName)))
    .join(' + ');
}

function firstNameOf(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] ?? displayName;
}

function teamsOf(group: GroupRound): TeamId[] {
  const used = new Set(group.participants.map((p) => p.team).filter((t): t is TeamId => t !== null));
  return (['A', 'B'] as TeamId[]).filter((t) => used.has(t));
}

/** Birdie, bogey e double+ — as três marcas que a legenda nomeia. */
function scoreClass(
  strokes: number | null,
  par: number,
  s: Record<string, string>,
): string | undefined {
  if (strokes === null) return undefined;
  const diff = strokes - par;
  if (diff <= -1) return s.birdie;
  if (diff === 1) return s.bogey;
  if (diff >= 2) return s.double;
  return undefined;
}

function segmentsFor(count: number) {
  if (count <= 9) return [{ key: 'TOT', from: 1, to: count }];
  const half = Math.floor(count / 2);
  return [
    { key: 'OUT', from: 1, to: half },
    { key: 'IN', from: half + 1, to: count },
    { key: 'TOT', from: 1, to: count },
  ];
}

function totalFor(
  holes: GroupRound['holes'],
  seg: { from: number; to: number },
  pick: (h: GroupRound['holes'][number]) => number | null,
): number | null {
  let sum = 0;
  let any = false;
  for (const h of holes) {
    if (h.holeNumber < seg.from || h.holeNumber > seg.to) continue;
    const v = pick(h);
    if (v == null) continue;
    sum += v;
    any = true;
  }
  return any ? sum : null;
}
